import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllSubCategories,
  clearAllSubCategories,
} from "../../actions/subcategoryActions";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Edit, CheckCircle, Cancel, Book } from '@mui/icons-material';
import LoadingBackdrop from "../Common/LoadingBackdrop";

const AudioBookTable = ({ searchText }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rawSubcategories = useSelector((state) => state.subcategories.subcategories);
  const loading = useSelector((state) => state.subcategories.isLoading);
  const subcategories = useMemo(
    () => rawSubcategories ? Object.values(rawSubcategories) : null,
    [rawSubcategories]
  );

  useEffect(() => {
    dispatch(clearAllSubCategories());
    dispatch(fetchAllSubCategories(1));
  }, [dispatch]);

  const handleEdit = (audiobook) => {
    navigate(`/audiobooks/edit/${audiobook.subcategory_id}`);
  };

  const renderAudioBooks = () => {
    if (!subcategories) return null;

    let data = subcategories.filter(obj => obj.subcategory_is_book);

    if (searchText !== "") {
      const search = searchText.toLowerCase();
      data = data.filter(
        (obj) => obj.subcategory_title.toLowerCase().includes(search)
      );
    }

    if (data.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {searchText ? "No audiobooks found matching your search" : "No audiobooks available"}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return data.map((obj) => (
      <TableRow
        key={obj.subcategory_id + obj.subcategory_title}
        sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
      >
        <TableCell>
          <Typography variant="subtitle1">{obj.category_title || "None"}</Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Audiobook">
              <IconButton size="small">
                <Book />
              </IconButton>
            </Tooltip>
            <Typography variant="subtitle1">{obj.subcategory_title}</Typography>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Typography
            sx={{
              color: obj.subcategory_is_active ? 'green' : 'red',
              fontWeight: 500,
            }}
          >
            {obj.subcategory_is_active ? 'Active' : 'Inactive'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Edit">
            <IconButton
              color="info"
              onClick={() => handleEdit(obj)}
              sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  if (loading && !subcategories) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <LoadingBackdrop open={loading && subcategories !== null} />
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Table aria-label="audiobooks table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderAudioBooks()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AudioBookTable;